import React, { useCallback, useRef, useState } from 'react';
import { useReactFlow } from 'reactflow';

interface UseNodeResizeOptions {
  id: string;
  data: any;
  width?: number;
  height?: number;
  defaultWidth: number;
  defaultHeight: number;
  minWidth: number;
  minHeight: number;
  onResize?: (width: number, height: number) => void;
  onResizeEnd?: (width: number, height: number) => void;
}

const updateNodeSize = (
  nodes: any[],
  nodeId: string,
  width: number,
  height: number | undefined,
  setNodeState: (updater: (nodes: any[]) => any[]) => void,
) => {
  setNodeState(nds =>
    nds.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          width,
          height,
          style: {
            ...node.style,
            width,
            height,
          },
        };
      }
      return node;
    }),
  );
};

export const useNodeResize = ({
  id,
  data,
  width,
  height,
  defaultWidth,
  defaultHeight,
  minWidth,
  minHeight,
  onResize,
  onResizeEnd,
}: UseNodeResizeOptions) => {
  const { setNodes: setNodeState, getNodes, getZoom } = useReactFlow();
  const [currentSize, setCurrentSize] = useState<{
    width: number;
    height: number | undefined;
  } | null>(null);
  const currentSizeRef = useRef<{ width: number; height: number | undefined } | null>(
    null,
  );
  const [isResizing, setIsResizing] = useState(false);

  const nodeWidth =
    currentSize?.width ??
    (width as number) ??
    data?.size?.width ??
    defaultWidth;
  const nodeHeight =
    currentSize?.height ??
    (height as number) ??
    data?.size?.height ??
    defaultHeight;

  const handleResize = useCallback(
    (event: any, params?: { width?: number; height?: number }) => {
      const nodes = getNodes();
      const currentNode = nodes.find(node => node.id === id);
      if (!currentNode) return;

      const newWidth =
        params?.width ??
        (currentNode.width as number) ??
        defaultWidth;
      const newHeight =
        params?.height ?? (currentNode.height as number) ?? defaultHeight;

      currentSizeRef.current = { width: newWidth, height: newHeight };
      setCurrentSize({ width: newWidth, height: newHeight });

      if (onResize) {
        onResize(newWidth, newHeight);
      }
    },
    [id, getNodes, defaultWidth, defaultHeight, onResize],
  );

  const handleResizeEnd = useCallback(() => {
    const finalSize = currentSizeRef.current;
    currentSizeRef.current = null;

    if (finalSize?.width && finalSize?.height) {
      updateNodeSize(getNodes(), id, finalSize.width, finalSize.height, setNodeState);
      if (onResizeEnd) {
        onResizeEnd(finalSize.width, finalSize.height);
      }
    }

    setCurrentSize(null);
  }, [id, getNodes, setNodeState, onResizeEnd]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsResizing(true);

      const nodeElement = document.querySelector(
        `[data-id="${id}"]`,
      ) as HTMLElement;
      if (!nodeElement) {
        setIsResizing(false);
        return;
      }

      const nodeRect = nodeElement.getBoundingClientRect();

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = nodeWidth;
      const startHeight = nodeHeight || nodeRect.height;

      let animationFrameId: number | null = null;
      let pendingUpdate: { width: number; height: number } | null = null;

      const updateSize = () => {
        if (pendingUpdate) {
          handleResize(null, pendingUpdate);
          pendingUpdate = null;
        }
        animationFrameId = null;
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();

        const currentZoom = getZoom();
        const deltaX = (moveEvent.clientX - startX) / currentZoom;
        const deltaY = (moveEvent.clientY - startY) / currentZoom;

        const newWidth = Math.max(minWidth, startWidth + deltaX);
        const newHeight = Math.max(minHeight, startHeight + deltaY);

        pendingUpdate = { width: newWidth, height: newHeight };

        if (!animationFrameId) {
          animationFrameId = requestAnimationFrame(updateSize);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }

        if (pendingUpdate) {
          handleResize(null, pendingUpdate);
        }

        handleResizeEnd();
        setIsResizing(false);
      };

      document.addEventListener('mousemove', handleMouseMove, {
        passive: false,
      });
      document.addEventListener('mouseup', handleMouseUp);
    },
    [id, nodeWidth, nodeHeight, handleResize, handleResizeEnd, getZoom, minWidth, minHeight],
  );

  return {
    nodeWidth,
    nodeHeight,
    handleResize,
    handleResizeEnd,
    handleResizeStart,
    isResizing,
  };
};

